/* eslint-disable no-unused-vars */
import { useState, useContext, useEffect } from "react";
import { AdminContext } from "../../../context/AdminContext";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Globe, Star, Pin, Eye } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";

const BlogPreview = () => {
  const { blogId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { fetchBlogById } = useContext(AdminContext);

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState("en");

  const localizedTitle =
    lang === "am" ? blog?.titleAm || blog?.titleEn : blog?.titleEn || blog?.titleAm;
  const localizedSummary =
    lang === "am"
      ? blog?.summaryAm || blog?.summaryEn
      : blog?.summaryEn || blog?.summaryAm;
  const localizedContent =
    lang === "am"
      ? blog?.contentAm || blog?.contentEn
      : blog?.contentEn || blog?.contentAm;
  const localizedCategoryName = blog?.category?.nameEn || "Uncategorized";

  useEffect(() => {
    loadBlog();
  }, [blogId]);

  const loadBlog = async () => {
    const data = await fetchBlogById(blogId);
    if (data?.success) {
      setBlog(data.blog);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500 dark:text-dark-muted">Blog not found</p>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-500" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-text dark:text-dark-text flex items-center gap-2">
              <Eye size={20} className="text-primary" /> Preview
            </h1>
            <p className="text-sm text-gray-500 dark:text-dark-muted">
              This is how the blog will appear to parents
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setLang("en")}
            className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${
              lang === "en" ? "bg-primary text-white" : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-dark-muted"
            }`}
          >
            🇬🇧 EN
          </button>
          <button
            onClick={() => setLang("am")}
            className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-colors ${
              lang === "am" ? "bg-primary text-white" : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-dark-muted"
            }`}
          >
            🇪🇹 AM
          </button>
        </div>
      </div>

      {/* Preview Card */}
      <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
        {/* Cover Image */}
        {blog.coverImage && (
          <img
            src={blog.coverImage}
            alt={localizedTitle || "Blog cover"}
            className="w-full h-64 sm:h-80 object-cover"
          />
        )}

        {/* Content */}
        <div className="p-6 sm:p-10">
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            {blog.category && (
              <span
                className="px-3 py-1 rounded-full text-xs font-bold"
                style={{
                  backgroundColor: `${blog.category.color}15`,
                  color: blog.category.color,
                }}
              >
                {blog.category.icon ? `${blog.category.icon} ` : ""}
                {localizedCategoryName}
              </span>
            )}
            <span className="text-sm text-gray-400 flex items-center gap-1">
              <Clock size={14} /> {blog.readingTime} min read
            </span>
            <span className="text-sm text-gray-400 flex items-center gap-1">
              <Calendar size={14} />{" "}
              {new Date(blog.publishedAt || blog.createdAt).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </span>
            {blog.isFeatured && <Star size={14} className="text-yellow-500 fill-yellow-500" />}
            {blog.isPinned && <Pin size={14} className="text-primary fill-primary" />}
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-text dark:text-dark-text mb-4 leading-tight">
            {localizedTitle || "Untitled"}
          </h1>

          {/* Summary */}
          {localizedSummary && (
            <p className="text-lg text-gray-500 dark:text-dark-muted mb-8 leading-relaxed italic">
              {localizedSummary}
            </p>
          )}

          {/* Author */}
          {blog.author && (
            <div className="flex items-center gap-3 mb-8 pb-8 border-b border-gray-100 dark:border-dark-border">
              {blog.author.image ? (
                <img
                  src={blog.author.image}
                  alt={blog.author.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                  {blog.author.name?.charAt(0)}
                </div>
              )}
              <div>
                <p className="font-semibold text-text dark:text-dark-text text-sm">
                  {blog.author.name}
                </p>
                <p className="text-xs text-gray-400">Author</p>
              </div>
            </div>
          )}

          {/* Content */}
          <div
            className="prose prose-lg dark:prose-invert max-w-none leading-relaxed text-text dark:text-dark-text"
            dangerouslySetInnerHTML={{
              __html: localizedContent || "<p>No content</p>",
            }}
          />

          {/* Tags */}
          {blog.tags && blog.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-8 pt-8 border-t border-gray-100 dark:border-dark-border">
              {blog.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-dark-muted text-xs font-medium rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 mt-6 justify-center">
        <button
          onClick={() => navigate(`/parent-hub/blogs/edit/${blogId}`)}
          className="px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-colors"
        >
          Edit Blog
        </button>
        <button
          onClick={() => navigate("/parent-hub/blogs")}
          className="px-6 py-3 border border-gray-200 dark:border-dark-border text-gray-600 dark:text-dark-muted rounded-xl font-bold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Back to List
        </button>
      </div>
    </div>
  );
};

export default BlogPreview;
